/**
 * Admin User Card Component
 *
 * Displays an admin user's information in a card format with:
 * - Avatar and name
 * - Role badge
 * - Status indicator (active/inactive)
 * - Last login timestamp
 * - Action menu (edit, reset password, toggle status, delete)
 */
import { motion } from 'framer-motion';
import { cn, Card, CardContent, Button } from '@respectly/ui';
import {
  MoreHorizontal,
  Mail,
  Clock,
  Edit,
  KeyRound,
  UserX,
  UserCheck,
  Trash2,
  User,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@respectly/ui';
import type { SuperAdminUserListItem } from '@respectly/shared-types';
import { AdminRoleBadge } from './AdminRoleBadge';

// ============================================
// Component Props
// ============================================

export interface AdminUserCardProps {
  user: SuperAdminUserListItem;
  onEdit?: () => void;
  onResetPassword?: () => void;
  onToggleActive?: () => void;
  onDelete?: () => void;
  className?: string;
}

// ============================================
// Helper Functions
// ============================================

function formatLastLogin(date: Date | null | undefined): string {
  if (!date) return 'Never logged in';

  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: days > 365 ? 'numeric' : undefined,
  });
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

// ============================================
// Component
// ============================================

export function AdminUserCard({
  user,
  onEdit,
  onResetPassword,
  onToggleActive,
  onDelete,
  className,
}: AdminUserCardProps) {
  const hasActions = onEdit || onResetPassword || onToggleActive || onDelete;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={cn(
          'relative overflow-hidden transition-all duration-200',
          'border-slate-200/60 dark:border-slate-700/60',
          'hover:border-slate-300 dark:hover:border-slate-600',
          'hover:shadow-md',
          !user.isActive && 'opacity-75',
          className
        )}
      >
        {/* Status indicator bar */}
        <div
          className={cn(
            'absolute top-0 left-0 w-full h-1',
            user.isActive
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
              : 'bg-gradient-to-r from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700'
          )}
        />

        <CardContent className="p-4 pt-5">
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div
              className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                'font-semibold text-lg',
                user.isActive
                  ? 'bg-gradient-to-br from-violet-100 to-violet-200 text-violet-700 dark:from-violet-900/40 dark:to-violet-800/40 dark:text-violet-300'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
              )}
            >
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-full h-full rounded-xl object-cover"
                />
              ) : (
                getInitials(user.firstName, user.lastName)
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                    {user.firstName} {user.lastName}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                </div>

                {/* Actions menu */}
                {hasActions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {onEdit && (
                        <DropdownMenuItem onClick={onEdit}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                      )}
                      {onResetPassword && (
                        <DropdownMenuItem onClick={onResetPassword}>
                          <KeyRound className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                      )}
                      {onToggleActive && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={onToggleActive}>
                            {user.isActive ? (
                              <>
                                <UserX className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                        </>
                      )}
                      {onDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={onDelete}
                            className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Role and status */}
              <div className="flex items-center gap-2 mt-3">
                <AdminRoleBadge role={user.role} size="sm" />
                {!user.isActive && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                    Inactive
                  </span>
                )}
              </div>

              {/* Last login */}
              <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-400 dark:text-slate-500">
                <Clock className="w-3.5 h-3.5" />
                <span>Last login: {formatLastLogin(user.lastLoginAt)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton Component
// ============================================

export function AdminUserCardSkeleton({ className }: { className?: string }) {
  return (
    <Card
      className={cn(
        'border-slate-200/60 dark:border-slate-700/60',
        className
      )}
    >
      <div className="h-1 bg-slate-200 dark:bg-slate-700 animate-pulse" />
      <CardContent className="p-4">
        <div className="flex items-start gap-3 animate-pulse">
          <div className="w-12 h-12 rounded-xl bg-slate-200 dark:bg-slate-700" />
          <div className="flex-1 space-y-3">
            <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="h-6 w-20 bg-slate-200 dark:bg-slate-700 rounded-full" />
            <div className="h-3 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Grid Component
// ============================================

export function AdminUserCardGrid({
  users,
  onEdit,
  onResetPassword,
  onToggleActive,
  onDelete,
  className,
}: {
  users: SuperAdminUserListItem[];
  onEdit?: (user: SuperAdminUserListItem) => void;
  onResetPassword?: (user: SuperAdminUserListItem) => void;
  onToggleActive?: (user: SuperAdminUserListItem) => void;
  onDelete?: (user: SuperAdminUserListItem) => void;
  className?: string;
}) {
  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <User className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
          No admin users found
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Create your first admin user to get started.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-4',
        'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className
      )}
    >
      {users.map((user, index) => (
        <motion.div
          key={user.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
        >
          <AdminUserCard
            user={user}
            onEdit={onEdit ? () => onEdit(user) : undefined}
            onResetPassword={onResetPassword ? () => onResetPassword(user) : undefined}
            onToggleActive={onToggleActive ? () => onToggleActive(user) : undefined}
            onDelete={onDelete ? () => onDelete(user) : undefined}
          />
        </motion.div>
      ))}
    </div>
  );
}
